import type { FiveSymbolLocale } from '#common/types/locale';
import type { Subscription } from '#common/types/model/subscription';
import type {
  NewsArticle,
  NewsArticleLocalizedContents,
} from '#common/types/news';
import type { NewsArticles, NewsArticleWithPlanId } from './types';

import { AbstractBlock } from '#common/types/blocks';
import { fiveSymbolsMap, twoSymbolsMap } from '#common/types/locale';

export const MIXED_NEWS_LAYOUT = 'mixed';
export const HORIZONTAL_NEWS_LAYOUT = 'horizontal';
export const SMALL_NEWS_LAYOUT = 'small';
export const SLIDER_NEWS_LAYOUT = 'slider';
export const NEWS_LAYOUTS = [
  MIXED_NEWS_LAYOUT,
  HORIZONTAL_NEWS_LAYOUT,
  SMALL_NEWS_LAYOUT,
  SLIDER_NEWS_LAYOUT,
];

export const DEFAULT_IMAGE = 'default_image';
export const MOBILE_SCREEN_WIDTH = 480;
export const SMALL_NEWS_AMOUNT = 3;
export const STANDARD_NEWS_AMOUNT = 6;
export const TINYMCE_PLUGINS =
  'preview fullpage searchreplace autolink directionality visualblocks visualchars image link media template codesample table charmap hr pagebreak nonbreaking anchor toc insertdatetime lists textcolor wordcount imagetools contextmenu colorpicker textpattern';
export const TINYMCE_MENUBAR = 'edit view insert format table';

export const requiredFields = ['title', 'image', 'text_short', 'text_long'];

export const getLauncherIdFromBlock = (block: AbstractBlock): string => {
  const launcherComponent = block.components.find(
    ({ type }) => type === 'launcher_select'
  );
  if (!launcherComponent || !launcherComponent.value) {
    return '';
  }
  return launcherComponent.value as string;
};

export const getNewsArticleComponents = (
  block: AbstractBlock,
  newsArticles: NewsArticles
): any[] => {
  const launcherId = getLauncherIdFromBlock(block);
  if (
    !launcherId ||
    !newsArticles[launcherId] ||
    !newsArticles[launcherId].news
  ) {
    return [];
  }
  return newsArticles[launcherId].news.map((news) => ({
    _id: news.newsData.id,
    type: 'news_article',
    isDragDisabled: true,
    news,
  }));
};

export const getNewsArticleMissingComponents = (
  block: AbstractBlock
): string[] => {
  const launcherId = getLauncherIdFromBlock(block);
  if (!launcherId) {
    return [];
  }
  return ['news_article'];
};

export const findLocalizedNewsContent = (
  newsData: NewsArticle,
  locale: FiveSymbolLocale
): NewsArticleLocalizedContents | undefined =>
  newsData.localized_contents.find(
    (news) => news.locale === fiveSymbolsMap[locale]
  ) || newsData.localized_contents.find((news) => news.locale === 'en');

export const getNewsByLocale = ({
  block,
  launcherData,
  locale,
}: {
  block: AbstractBlock;
  launcherData: {
    newsArticles: NewsArticles;
  };
  locale: FiveSymbolLocale;
}): {
  newsCount: number;
  news: NewsArticleWithPlanId[];
  launcherId: string;
} => {
  const launcherId = getLauncherIdFromBlock(block);

  let newsCount = 0;
  let news: NewsArticleWithPlanId[] = [];
  const newsArticles = launcherData.newsArticles[launcherId];
  if (newsArticles) {
    newsCount = newsArticles.count;
    news = newsArticles.news.map(({ newsData }) => {
      const localizedNewsContent = findLocalizedNewsContent(newsData, locale);
      return { ...newsData, ...localizedNewsContent };
    });
  }
  return { newsCount, news, launcherId };
};

export const formatNewsDate = (newsDate: number): Date => {
  const date = new Date(0);
  date.setSeconds(newsDate);
  return date;
};

export const getNewsHash = (title: string): string =>
  `#${encodeURI(title.toLocaleLowerCase().replace(/\s/g, '-'))}`;

export const filterSubscriptionsNamesForNews = (
  subscriptionPlans: Subscription[],
  newsSubscriptionPlans: string[] = [],
  locale: FiveSymbolLocale
) =>
  subscriptionPlans
    .filter((plan) => newsSubscriptionPlans.includes(plan.external_id))
    .reduce(
      (accumulator, current) =>
        accumulator.some((plan) => plan.external_id === current.external_id)
          ? accumulator
          : [...accumulator, current],
      [] as Subscription[]
    )
    .map((plan) => plan.name?.[fiveSymbolsMap[locale]] || plan.name.en)
    .filter(Boolean)
    .map<string>((name) => `"${name}"`);

export const getNewsPrivateDescription = (
  source: string,
  planNames: string[]
) => source.replace('{subscription_name}', planNames.join(', '));

export const validateLocalizedContent = (
  content: NewsArticleLocalizedContents
): boolean =>
  requiredFields.every((field) => !!content[field]) &&
  content.image !== DEFAULT_IMAGE;

export const validation = (news: NewsArticleWithPlanId): boolean =>
  news.newsData.localized_contents.every(
    (contents: NewsArticleLocalizedContents) =>
      validateLocalizedContent(contents)
  );

export const getInitialInvalidFields = (languages: FiveSymbolLocale[]) =>
  languages.reduce((fields, language) => {
    fields[language] = requiredFields.reduce((res, reqField) => {
      res[reqField] = false;
      return res;
    }, {});
    return fields;
  }, {});

export const getInvalidLanguageFields = (
  newsContent: NewsArticleLocalizedContents
) =>
  requiredFields.reduce((fields, field) => {
    fields[field] =
      field === 'image'
        ? newsContent[field] === DEFAULT_IMAGE
        : !newsContent[field];
    return fields;
  }, {});

export const getInvalidFormFields = (news: NewsArticleWithPlanId) =>
  news.newsData.localized_contents.reduce((fields, content) => {
    fields[twoSymbolsMap[content.locale]] = getInvalidLanguageFields(content);
    return fields;
  }, {});
